import { forwardRef, useMemo } from 'react'
import { format as formatDate } from 'date-fns'

import { InputAdornment, TextField, TextFieldProps, useTheme } from '@mui/material'
import { DateRangeIcon } from '@mui/x-date-pickers'

const DEFAULT_FORMAT = 'DD/MM/YYYY'

type DateRangeFieldProps = Omit<TextFieldProps, 'value' | 'format'> & {
  startDate: Date | null
  endDate: Date | null
  format?: string
  width?: number | string
}

export const DateRangeField = forwardRef<HTMLDivElement, DateRangeFieldProps>(
  ({ startDate, endDate, format: dateFormat, width, label, error, onClick, ...props }, ref) => {
    const theme = useTheme()

    const formatString = dateFormat || DEFAULT_FORMAT
    const muiFormat = formatString.replace(/D/g, 'd').replace(/Y/g, 'y')

    const displayValue = useMemo(() => {
      if (!startDate || !endDate) return ''

      const start = startDate ? formatDate(startDate, muiFormat) : formatString
      const end = endDate ? formatDate(endDate, muiFormat) : formatString

      return `${start} - ${end}`
    }, [startDate, endDate, muiFormat, formatString])

    return (
      <TextField
        {...props}
        ref={ref}
        size="small"
        variant="outlined"
        label={label}
        value={displayValue}
        error={error}
        onClick={onClick}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <DateRangeIcon sx={{ opacity: 0.7 }} />
              </InputAdornment>
            ),
            sx: {
              cursor: 'pointer',
              height: 37
            }
          }
        }}
        sx={{
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          width,
          ...props.sx
        }}
      />
    )
  }
)

DateRangeField.displayName = 'DateRangeField'
